import * as React from 'react';

import styles from '@utmb/features/city-maps/city-maps.module.scss';
import { Font } from '@utmb/design-system/text/font';
import { useCityMaps } from '../../contexts/CityMapsContext';
import { LightenDarkenColor } from '@utmb/utils/colors';

const CityMapTabs = () => {
    const { isLoaded, pois, selectedRaceId, setSelectedRaceId, cities, color, colorIsDark, selectedCity, setSelectedCity } = useCityMaps();

    const raceTabs: TabsProps<number> = {
        onChangeTab: (id: number) => setSelectedRaceId(id),
        selectedId: selectedRaceId!,
        tabs: pois.map(({ raceId, race, color, colorIsDark }) => ({
            id: raceId,
            label: race,
            color,
            colorIsDark,
        })),
    };

    const cityTabs: TabsProps<string> = {
        onChangeTab: (id: string) => setSelectedCity(id),
        selectedId: selectedCity!,
        tabs: cities.map(({ name }) => ({
            id: name,
            label: name,
            color: color!,
            colorIsDark,
        })),
    };

    return <div className={styles.cityMapsTabs}>{isLoaded && [raceTabs, cityTabs].map((props: TabsProps<any>, idx) => <Tabs key={idx} {...props} />)}</div>;
};

export default CityMapTabs;

interface TabsProps<T extends string | number> {
    onChangeTab: (id: T) => void;
    selectedId: T;
    tabs: {
        id: T;
        label: string;
        color: string;
        colorIsDark: boolean;
    }[];
}

const Tabs = React.memo(<T extends string | number>({ onChangeTab, selectedId, tabs }: TabsProps<T>): React.ReactElement => {
    const handleClick = (id: T): void => {
        onChangeTab(id);
    };

    return (
        <div className={styles.tabsContainer}>
            {tabs.map(({ id, label, color, colorIsDark }) => (
                <button key={id} className={styles.item} onClick={() => handleClick(id)} style={getTabStyle(id, selectedId, color, colorIsDark)}>
                    <Font.FuturaHeavy desktop="20" mobile="16" as="span">
                        {label}
                    </Font.FuturaHeavy>
                </button>
            ))}
        </div>
    );
});

const getTabStyle = <T extends string | number>(current: T, selected: T, color: string, colorIsDark: boolean): Record<string, unknown> => {
    return {
        borderBottom: `solid ${isSelectedIndex(current, selected) ? `3px ${color}` : '2px lightgray'}`,
        color: isSelectedIndex(current, selected) ? (colorIsDark ? color : LightenDarkenColor(color)) : 'lightgray',
    };
};

const isSelectedIndex = <T extends string | number>(current: T, idx: T): boolean => {
    return current === idx;
};
